/**
 * The catering assortment — the first and main branch of Småken.
 *
 * Source: "Cateringunderlag Lilla Verket & Top Notch Bakery". Prices are the
 * partners' own, incl. 6 % VAT (livsmedelsmoms). Excl.-VAT figures are derived
 * with `exVat()` so there is only ever one number to update per product.
 *
 * Two vocabularies, kept apart on purpose:
 *  - `occasions` — when you eat it (Frukost, Fika, Lunch…). How people think.
 *  - `category`  — what it is (Mat, Dryck, Bak, Snacks). How people filter.
 * `section` is the partner's own menu heading, used to group their page.
 */

export type Occasion = 'Frukost' | 'Fika' | 'Lunch' | 'Mellanmål' | 'Middag';

export const OCCASIONS: Occasion[] = ['Frukost', 'Fika', 'Lunch', 'Mellanmål', 'Middag'];

export type CateringCategory = 'Mat' | 'Dryck' | 'Bak' | 'Snacks';

export const CATERING_CATEGORIES: CateringCategory[] = ['Mat', 'Dryck', 'Bak', 'Snacks'];

export type CateringUnit = 'per person' | 'per styck' | 'per bricka';

export const CATERING_UNITS: CateringUnit[] = ['per person', 'per styck', 'per bricka'];

/** The 14 allergens food businesses must declare (EU 1169/2011). */
export type Allergen =
  | 'Gluten'
  | 'Mjölk'
  | 'Ägg'
  | 'Nötter'
  | 'Jordnötter'
  | 'Soja'
  | 'Selleri'
  | 'Senap'
  | 'Sesam'
  | 'Fisk'
  | 'Kräftdjur'
  | 'Blötdjur'
  | 'Lupin'
  | 'Sulfit';

export const ALLERGENS: Allergen[] = [
  'Gluten', 'Mjölk', 'Ägg', 'Nötter', 'Jordnötter', 'Soja', 'Selleri',
  'Senap', 'Sesam', 'Fisk', 'Kräftdjur', 'Blötdjur', 'Lupin', 'Sulfit',
];

/** Positive dietary labels — what the guest is looking for, not what to avoid. */
export type Diet = 'Vegetarisk' | 'Vegansk' | 'Glutenfri' | 'Laktosfri';

export const DIETS: Diet[] = ['Vegetarisk', 'Vegansk', 'Glutenfri', 'Laktosfri'];

/** Swedish VAT on food, 6 % from April 2026. */
export const VAT_RATE = 0.06;

export interface CateringItem {
  id: string;
  name: string;
  /** The partner's own menu heading, e.g. "Mackverk" or "Frukostpaket". */
  section: string;
  category: CateringCategory;
  occasions: Occasion[];
  description: string;
  /** Price in SEK incl. 6 % VAT, for one unit of `unit`. 0 when priced on request. */
  price: number;
  /** Price is set per order (e.g. custom cakes). */
  onRequest?: boolean;
  unit: CateringUnit;
  minGuests: number;
  /** Days of notice the partner needs. */
  leadTime: number;
  image: string;
  /** CSS object-position for the card crop, when the product isn't centred in the photo. */
  imageFocus?: string;
  /** Full ingredient list, as the partner writes it. */
  ingredients?: string;
  /** Declared allergens. `undefined` = not yet declared; `[]` = declared free of all 14. */
  allergens?: Allergen[];
  diets?: Diet[];
  /** Hidden items stay in admin but never reach the public site. */
  hidden?: boolean;
}

const IMG = {
  mackverk: 'https://cdn.magicpatterns.com/patterns/generated-images/852d1220-5a9e-4223-89cd-6df74bd75862.jpg',
  figPig: 'https://cdn.magicpatterns.com/uploads/r8zYUuddv2KxF9hTqM7gZE/IMG_4319.png',
  beeBrie: 'https://cdn.magicpatterns.com/uploads/iodUee6UZ54GSCmbkXyv8k/IMG_4320.png',
  tunaMatata: 'https://cdn.magicpatterns.com/uploads/jrhmUTExGgGUjYUh6cAPXN/IMG_4321.png',
  beetBleatMenu: 'https://cdn.magicpatterns.com/uploads/jNT3zrBQX9Hm1DpJ5LcxYT/IMG_4318.png',
  beetBleat:
    'https://cdn.magicpatterns.com/uploads/uUGsKDQ9ZuyruUBxqGxjuF/749403242_17925808158367125_8423757437317615676_n..jpg',
  sallad: 'https://cdn.magicpatterns.com/patterns/generated-images/b7656e0d-a419-43e7-b253-099d3ac9df72.jpg',
  caesarKyckling: 'https://cdn.magicpatterns.com/uploads/b5K2LR62Q2djr8J3ry1NtT/IMG_2034.png',
  caesar:
    'https://cdn.magicpatterns.com/uploads/sK8V4Ra5SJVuTmtUd1G3ue/621744886_17977584278806262_511352703514262922_n..webp',
  frukost: 'https://cdn.magicpatterns.com/patterns/generated-images/e9ace02d-6487-4a90-a76f-966d14fb8e54.jpg',
  fralla: 'https://cdn.magicpatterns.com/patterns/generated-images/0c664b63-8d57-4f98-a722-f14bd09f5a50.jpg',
  dryck: 'https://cdn.magicpatterns.com/patterns/generated-images/4050d8b8-192a-4913-b31d-ae3b0cdf6186.jpg',
  bakverk: 'https://cdn.magicpatterns.com/patterns/generated-images/87a7a2f5-4da0-4bff-a9ef-2e5c266a1a62.jpg',
  matcha:
    'https://cdn.magicpatterns.com/uploads/x9G91wUGgW4PeT1pdNuVpb/551860788_17884842120367125_8041553542335615803_n..jpg',
  croissant:
    'https://cdn.magicpatterns.com/uploads/fV4bcFArGpq3Z9HPPZwC5X/586983853_17892207624367125_8566069967174582859_n..jpg',
  bullar: 'https://cdn.magicpatterns.com/patterns/generated-images/264b58b6-1c66-481c-9dc4-a443e6930eeb.jpg',
  bakelse:
    'https://cdn.magicpatterns.com/uploads/foff1feghzmgrktPEHa3kJ/479502353_18054317555511062_1164978928810660445_n..jpg',
  cheesecake: 'https://cdn.magicpatterns.com/patterns/generated-images/8fc743b5-2ac3-4a58-8c38-642530320605.jpg',
  tarta: 'https://cdn.magicpatterns.com/patterns/generated-images/96991e69-59d8-42b1-a2ff-3d943c1f0a9d.jpg',
  painAuChocolat: 'https://cdn.magicpatterns.com/uploads/4qBmGpCgz5JW3DXJnJjhWF/IMG_7967.png',
  chia: 'https://cdn.magicpatterns.com/uploads/bhoPnFsbyMgGR7Eczogc3E/IMG_4308.png',
  barMilk: 'https://cdn.magicpatterns.com/uploads/dEVx7amPksG6xN44nofSKz/IMG_1745.png',
  barCookiedough: 'https://cdn.magicpatterns.com/uploads/5KVEL59bRJAeJreFfgyxSm/IMG_1746.png',
  barHallon: 'https://cdn.magicpatterns.com/uploads/amPDpR8FkVUaAxoF9cnfPK/IMG_1751.png',
  barNuts: 'https://cdn.magicpatterns.com/uploads/jnc8cipWFrD1UhGT5ivMGF/IMG_1752.png',
};

/** Terse constructor so a whole menu stays readable as one table. */
const i = (
  id: string,
  section: string,
  name: string,
  category: CateringCategory,
  occasions: Occasion[],
  price: number,
  image: string,
  description: string,
  extra: Partial<Pick<CateringItem, 'unit' | 'minGuests' | 'leadTime' | 'onRequest' | 'imageFocus'>> = {}
): CateringItem => ({
  id,
  section,
  name,
  category,
  occasions,
  price,
  image,
  description,
  unit: extra.unit ?? 'per styck',
  minGuests: extra.minGuests ?? 1,
  leadTime: extra.leadTime ?? 1,
  onRequest: extra.onRequest,
  imageFocus: extra.imageFocus,
});

/** Top Notch bar photos: the bar sits low in front of their box. */
const BAR = { imageFocus: 'center 82%' };

const MORGON: Occasion[] = ['Frukost', 'Mellanmål'];
const MACKA: Occasion[] = ['Lunch', 'Mellanmål'];
const SALLAD: Occasion[] = ['Lunch', 'Middag'];
const FIKA: Occasion[] = ['Fika', 'Mellanmål'];
const DRYCK: Occasion[] = ['Frukost', 'Fika', 'Lunch', 'Mellanmål', 'Middag'];

export const CATERING: Record<string, CateringItem[]> = {
  'lilla-verket': [
    // Frukost
    i('lv-fr-1', 'Frukost', 'Frukostfralla ost & grönt', 'Mat', ['Frukost'], 52, IMG.fralla,
      'Fralla med ost och grönt.'),
    i('lv-fr-2', 'Frukost', 'Frukostfralla ost & kalkon', 'Mat', ['Frukost'], 55, IMG.fralla,
      'Fralla med ost och kalkon.'),
    i('lv-fr-3', 'Frukost', 'Frukostchia', 'Mat', MORGON, 0, IMG.chia,
      'Matchachia toppad med hallonpuré och rostad kokos.',
      { onRequest: true, imageFocus: 'center 70%' }),

    // Mackverk
    i('lv-ma-1', 'Mackverk', 'Fig & Pig', 'Mat', MACKA, 145, IMG.figPig,
      'Taleggio, prosciutto, sockerärts- och majsröra, dill, citron, fikonmarmelad.'),
    i('lv-ma-2', 'Mackverk', 'Beet & Bleat', 'Mat', MACKA, 139, IMG.beetBleatMenu,
      'Chèvre, rödbeta, grönkål, valnötter, citron, granatbalsam, chilihonung.'),
    i('lv-ma-3', 'Mackverk', 'Bee & Brie', 'Mat', MACKA, 145, IMG.beeBrie,
      'Getostkräm, brie, salami, fänkål, spenat, marinerade oliver, salladslök, chilihonung.'),
    i('lv-ma-4', 'Mackverk', 'Tuna Matata', 'Mat', MACKA, 129, IMG.tunaMatata,
      'Tonfiskröra, spenat, tomat, silverlök.'),

    // Sallad
    i('lv-sa-1', 'Sallad', 'Peas of Mind', 'Mat', SALLAD, 155, IMG.sallad,
      'Grönkål, salladskål, cosmopolitansallad, getostkräm, sockerärts- och majsröra, dill, fänkål, prosciutto, bacon, toppad med fikonmarmelad.'),
    i('lv-sa-2', 'Sallad', 'Tune In', 'Mat', SALLAD, 139, IMG.sallad,
      'Grönkål, salladskål, cosmopolitansallad, tonfiskröra, fänkål, cornichons, syltlök, gröna ärtor, kikärtor, marinerade oliver, honey lemon.'),
    i('lv-sa-3', 'Sallad', 'Beet It', 'Mat', SALLAD, 149, IMG.sallad,
      'Grönkål, salladskål, cosmopolitan, chèvre, rödbeta, picklad rödkål, valnötter, chilihonung.'),

    // Fika
    i('lv-fi-1', 'Fika', 'Pain au Chocolat', 'Bak', FIKA, 55, IMG.painAuChocolat, 'Smördeg och mörk choklad.'),
    i('lv-fi-2', 'Fika', 'Vaniljbulle', 'Bak', FIKA, 47, IMG.bullar, 'Bulle med vaniljkräm.'),
    i('lv-fi-3', 'Fika', 'Kanelbulle', 'Bak', FIKA, 42, IMG.bullar, 'Klassisk kanelbulle.'),
    i('lv-fi-4', 'Fika', 'Brownie', 'Bak', FIKA, 59, IMG.bakverk, 'Kladdig chokladbrownie.'),
    i('lv-fi-5', 'Fika', 'Cookie Brownie', 'Bak', FIKA, 49, IMG.bakverk, 'Cookie med browniekänsla.'),
    i('lv-fi-6', 'Fika', 'Cookie Matcha', 'Bak', FIKA, 52, IMG.matcha, 'Cookie med matcha.'),
    i('lv-fi-7', 'Fika', 'Basque Cheesecake Vanilj', 'Bak', ['Fika', 'Middag'], 70, IMG.cheesecake,
      'Bränd baskisk cheesecake med vanilj.'),
    i('lv-fi-8', 'Fika', 'Basque Cheesecake Pistage', 'Bak', ['Fika', 'Middag'], 70, IMG.cheesecake,
      'Bränd baskisk cheesecake med pistage.'),
  ],

  'top-notch-bakery': [
    // Frukostpaket
    i('tn-fp-1', 'Frukostpaket', 'Lilla frukostpaketet', 'Mat', ['Frukost'], 75, IMG.frukost,
      'Fralla och juice.', { unit: 'per person' }),
    i('tn-fp-2', 'Frukostpaket', 'Mellan frukostpaketet', 'Mat', ['Frukost'], 125, IMG.frukost,
      'Fralla, juice och yoghurt eller overnight oats.', { unit: 'per person' }),
    i('tn-fp-3', 'Frukostpaket', 'Stora frukostpaketet', 'Mat', ['Frukost'], 155, IMG.frukost,
      'Fralla, juice, ingefärsshot och yoghurt eller overnight oats.', { unit: 'per person' }),
    i('tn-fp-4', 'Frukostpaket', 'Bagelfrukost', 'Mat', ['Frukost'], 65, IMG.frukost, 'Frukost med bagel.', {
      unit: 'per person',
    }),

    // Frukost
    i('tn-fr-1', 'Frukost', 'Halv ostfralla', 'Mat', MORGON, 35, IMG.fralla, 'Halv fralla med ost.'),
    i('tn-fr-2', 'Frukost', 'Halv skinkfralla', 'Mat', MORGON, 35, IMG.fralla, 'Halv fralla med skinka.'),
    i('tn-fr-3', 'Frukost', 'Ostfralla', 'Mat', MORGON, 49, IMG.fralla, 'Fralla med ost.'),
    i('tn-fr-4', 'Frukost', 'Skinkfralla', 'Mat', MORGON, 49, IMG.fralla, 'Fralla med skinka.'),
    i('tn-fr-5', 'Frukost', 'Ost- & skinkfralla', 'Mat', MORGON, 49, IMG.fralla, 'Fralla med ost och skinka.'),
    i('tn-fr-6', 'Frukost', 'Salami- & briefralla', 'Mat', MORGON, 49, IMG.fralla, 'Fralla med salami och brie.'),
    i('tn-fr-7', 'Frukost', 'Ägg- & avokadofralla', 'Mat', MORGON, 55, IMG.fralla, 'Fralla med ägg och avokado.'),
    i('tn-fr-8', 'Frukost', 'Yoghurt', 'Mat', MORGON, 59, IMG.frukost, 'Yoghurt.'),
    i('tn-fr-9', 'Frukost', 'Overnight oats', 'Mat', MORGON, 59, IMG.frukost, 'Havre som fått dra över natten.'),
    i('tn-fr-10', 'Frukost', 'Ägg & kaviar', 'Mat', ['Frukost'], 25, IMG.frukost, 'Ägg med kaviar.'),

    // Sallad
    i('tn-sa-1', 'Sallad', 'Caesarsallad – Kyckling', 'Mat', SALLAD, 159, IMG.caesarKyckling,
      'Romansallad, kyckling, bacon, krutonger, rikligt med riven parmesan och färsk timjan.'),
    i('tn-sa-2', 'Sallad', 'Caesarsallad – Räka', 'Mat', SALLAD, 179, IMG.caesar, 'Caesarsallad med räkor.'),
    i('tn-sa-3', 'Sallad', 'Caesarsallad – Halloumi', 'Mat', SALLAD, 159, IMG.caesar, 'Caesarsallad med halloumi.'),

    // Fika
    i('tn-fi-1', 'Fika', 'Lyxig chokladboll', 'Bak', FIKA, 31, IMG.bakverk, 'Chokladboll, fast lyxigare.'),
    i('tn-fi-2', 'Fika', 'Hallonbrownie', 'Bak', FIKA, 45, IMG.barHallon,
      'Brownie doppad i mörk choklad, toppad med hallonchoklad.', BAR),
    i('tn-fi-3', 'Fika', 'Coco Choco', 'Bak', FIKA, 45, IMG.barMilk,
      'Kokos, helt doppad i mjölkchoklad.', BAR),
    i('tn-fi-4', 'Fika', 'Kladdkaka Cookiedough', 'Bak', FIKA, 45, IMG.barCookiedough,
      'Kladdkaka doppad i mjölkchoklad, toppad med kakdeg och chokladbitar.', BAR),
    i('tn-fi-5', 'Fika', 'Peanut Prince', 'Bak', FIKA, 45, IMG.bakverk, 'Jordnöt i huvudrollen.'),
    i('tn-fi-6', 'Fika', 'Schweizernöt', 'Bak', FIKA, 45, IMG.bakverk, 'Bakverk med nöt.'),
    i('tn-fi-7', 'Fika', "Let's Go Nuts", 'Bak', FIKA, 45, IMG.barNuts,
      'Doppad i mjölkchoklad, toppad med karamelliserade nötter.', BAR),
    i('tn-fi-8', 'Fika', 'Mandelblondie', 'Bak', FIKA, 45, IMG.bakverk, 'Blondie med mandel.'),
    i('tn-fi-9', 'Fika', 'Biskvi', 'Bak', FIKA, 40, IMG.bakverk, 'Klassisk biskvi.'),
    i('tn-fi-10', 'Fika', 'Veckans bakelse', 'Bak', ['Fika', 'Middag'], 70, IMG.bakelse,
      'Byts varje vecka. Fråga oss vad som gäller just er leveransvecka.'),
    i('tn-fi-11', 'Fika', 'Cheesecake', 'Bak', ['Fika', 'Middag'], 55, IMG.cheesecake, 'Cheesecake.'),

    // Kall dryck
    i('tn-dr-1', 'Kall dryck', 'Ramlösa', 'Dryck', DRYCK, 35, IMG.dryck, 'Kolsyrat mineralvatten.'),
    i('tn-dr-2', 'Kall dryck', 'Läsk', 'Dryck', DRYCK, 35, IMG.dryck, 'Läsk.'),
    i('tn-dr-3', 'Kall dryck', 'Äppeljuice', 'Dryck', DRYCK, 39, IMG.dryck, 'Äppeljuice.'),
    i('tn-dr-4', 'Kall dryck', 'Apelsinjuice', 'Dryck', DRYCK, 39, IMG.dryck, 'Apelsinjuice.'),
    i('tn-dr-5', 'Kall dryck', 'Häppi', 'Dryck', DRYCK, 39, IMG.dryck, 'Häppi.'),
    i('tn-dr-6', 'Kall dryck', 'Festis', 'Dryck', DRYCK, 23, IMG.dryck, 'Festis.'),
    i('tn-dr-7', 'Kall dryck', 'Lemonad', 'Dryck', DRYCK, 39, IMG.dryck, 'Lemonad.'),
    i('tn-dr-8', 'Kall dryck', 'Smoothie', 'Dryck', ['Frukost', 'Mellanmål'], 55, IMG.dryck, 'Smoothie.'),

    // Tårtor
    i('tn-ta-1', 'Tårtor', 'Tårta – vid förfrågan', 'Bak', ['Fika', 'Middag'], 0, IMG.tarta,
      'Pris och utförande efter förfrågan. Allergener varierar beroende på tårta.',
      { onRequest: true, leadTime: 3 }),
  ],
};

/** Price excl. VAT, unrounded. */
export const exVat = (priceInclVat: number): number => priceInclVat / (1 + VAT_RATE);
